import { PdfImage, PdfWriter, Rgb, printable } from './pdf-writer';

/**
 * The board as a PDF (A4 portrait): title, note, time window, creation time and the board's deep link
 * (printed without title and note, clickable with them), then every chart — rendered offscreen in the light theme — with its title and
 * the stats of its visible series; at most two charts per page, page numbers in the footer. A text item (the settings
 * widget) flows in the board's order: lines and tables, continued on the next page where the page ends.
 */

export interface PdfStat { label: string; color: string; dash: number[]; text: string; }
export interface PdfChart {
  title: string;
  /** JPEG of the chart (canvas pixels). */
  jpeg: { bytes: Uint8Array; w: number; h: number };
  stats: PdfStat[];
  /** No data in the window. */
  empty: boolean;
  /** A table below the stats (the valve evaluation: one line per room). */
  table?: PdfTable;
}
/** A table: a header, then one line per row; the first column left-aligned, the others right-aligned; a row's
 *  `bar` (0..1) as a small gauge in its `color` at the start of its second column. */
export interface PdfTable {
  title?: string;
  head: string[];
  rows: { cells: string[]; bar?: number | null; color?: string | null }[];
  /** A line below it (e.g. what "≈" means). */
  note?: string;
  /** Column widths (shares of the width, in order) and alignment (default left): long cells and the note wrap
   *  (a row as high as its longest cell). Without: the first column 40 % left-aligned, the others right-aligned,
   *  cells cut to their column. */
  cols?: { w: number; align?: 'left' | 'right' }[];
}
/** A board item without a chart (the settings widget): its title, then its blocks top to bottom. */
export interface PdfText {
  kind: 'text';
  title: string;
  blocks: PdfBlock[];
}
/** A wrapped paragraph (`gap`: points above it; `bullet`: "•" before it, its lines indented) or a table. */
export type PdfBlock = { text: string; muted?: boolean; bold?: boolean; gap?: number; bullet?: boolean } | { table: PdfTable };
/** One item of the board, in the board's order. */
export type PdfItem = PdfChart | PdfText;
export interface PdfBoard {
  title: string;
  note: string;
  window: string;
  created: string;
  /** The board link the PDF's link area opens (title and note included). */
  link: string;
  /** The link as printed (may be shorter: without title and note, which are printed above). */
  linkText: string;
  charts: PdfItem[];
  /** Translated labels. */
  labels: { window: string; created: string; link: string; noData: string; page: (n: number, total: number) => string };
}

const MARGIN_X = 42;
const TOP = 44;
const BOTTOM = 46;
const INK: Rgb = [33, 37, 41];
const MUTED: Rgb = [108, 117, 125];
const RULE: Rgb = [206, 212, 218];
const LINK: Rgb = [13, 110, 253];

export function boardPdf(b: PdfBoard): Uint8Array {
  const pdf = new PdfWriter();
  const W = pdf.width, H = pdf.height;
  const cw = W - 2 * MARGIN_X;
  pdf.addPage();
  let y = TOP;
  /** Room for `h` more points below y on this page, else continue on a new one (a long note, a long link). */
  const room = (h: number) => {
    if (y > TOP && y + h > H - BOTTOM) { pdf.addPage(); y = TOP; }
  };

  // ── header ──
  for (const line of pdf.wrap(b.title, cw, 18, true)) { room(22); y += 18; pdf.text(MARGIN_X, y, line, { size: 18, bold: true, color: INK }); y += 4; }
  if (b.note.trim()) {
    y += 4;
    for (const line of pdf.wrap(b.note, cw, 10)) { room(12); y += 12; pdf.text(MARGIN_X, y, line, { size: 10, color: INK }); }
  }
  y += 18;
  const labelW = Math.max(pdf.textWidth(b.labels.window + ':', 9, true), pdf.textWidth(b.labels.created + ':', 9, true),
    pdf.textWidth(b.labels.link + ':', 9, true)) + 6;
  room(13);
  pdf.text(MARGIN_X, y, b.labels.window + ':', { size: 9, bold: true, color: INK });
  pdf.text(MARGIN_X + labelW, y, b.window, { size: 9, color: INK });
  y += 13;
  room(13);
  pdf.text(MARGIN_X, y, b.labels.created + ':', { size: 9, bold: true, color: INK });
  pdf.text(MARGIN_X + labelW, y, b.created, { size: 9, color: INK });
  y += 13;
  const linkLines = pdf.wrap(b.linkText, cw - labelW, 7.5);
  linkLines.forEach((line, i) => {
    room(9.5);
    if (i === 0) pdf.text(MARGIN_X, y, b.labels.link + ':', { size: 9, bold: true, color: INK });
    pdf.text(MARGIN_X + labelW, y, line, { size: 7.5, color: LINK });
    pdf.link(MARGIN_X + labelW, y - 7.5, pdf.textWidth(line, 7.5), 9.5, b.link);
    y += 9.5;
  });
  y += 4;
  pdf.line(MARGIN_X, y, W - MARGIN_X, y, RULE, 0.6);
  y += 14;

  // ── charts: at most two per page; one that does not fit below the header starts the next page ──
  let onPage = 0;
  const newPage = () => { pdf.addPage(); onPage = 0; return TOP; };
  for (const c of b.charts) {
    if ('kind' in c) {
      // a text item: on this page if its title and what follows it there fit (its lead: the lines below the title up
      // to the start of the first table, as that table would start), and fewer than two charts are on it
      if (onPage === 2 || (y > TOP && y + textLeadHeight(pdf, c, cw) > H - BOTTOM)) y = newPage();
      y += 12;
      pdf.text(MARGIN_X, y, pdf.fit(c.title, cw, 11.5, true), { size: 11.5, bold: true, color: INK });
      y += 4;
      for (const block of c.blocks) {
        if ('table' in block) { y = drawTable(pdf, block.table, y, cw, newPage); continue; }
        const size = TEXT_SIZE, lh = TEXT_LH, indent = block.bullet ? 10 : 0;
        y += block.gap ?? 2;
        pdf.wrap(block.text, cw - indent, size, !!block.bold).forEach((line, k) => {
          if (y + lh > H - BOTTOM) y = newPage();
          y += lh;
          if (block.bullet && k === 0) pdf.text(MARGIN_X + 2, y - 2, '•', { size, color: INK });
          pdf.text(MARGIN_X + indent, y - 2, line, { size, bold: !!block.bold, color: block.muted ? MUTED : INK });
        });
      }
      y += 16;
      onPage++;
      continue;
    }
    const imgH = cw * c.jpeg.h / c.jpeg.w;
    const statLines = layoutStats(pdf, c, cw);
    const blockH = 16 + imgH + 6 + statLines.length * 11 + (c.table ? tableHeight(c.table) : 0) + 16;
    if (onPage === 2 || (y > TOP && y + blockH > H - BOTTOM)) {
      pdf.addPage();
      y = TOP;
      onPage = 0;
    }
    y += 12;
    pdf.text(MARGIN_X, y, pdf.fit(c.title, cw, 11.5, true), { size: 11.5, bold: true, color: INK });
    y += 6;
    const img: PdfImage = pdf.addJpeg(c.jpeg.bytes, c.jpeg.w, c.jpeg.h);
    pdf.drawImage(img, MARGIN_X, y, cw, imgH);
    pdf.rect(MARGIN_X, y, cw, imgH, null, RULE, 0.5);
    if (c.empty) {
      const mid = y + imgH / 2;
      pdf.rect(MARGIN_X + cw / 2 - 110, mid - 14, 220, 22, [248, 249, 250], RULE, 0.5);
      pdf.text(MARGIN_X + cw / 2, mid + 1, b.labels.noData, { size: 9, color: MUTED, align: 'center' });
    }
    y += imgH + 6;
    for (const row of statLines) {
      y += 9;
      for (const cell of row) {
        if (!cell.more) pdf.line(cell.x, y - 3, cell.x + 12, y - 3, hexRgb(cell.color), 1.6, cell.dash.length ? cell.dash.map(d => d * 0.6) : []);
        pdf.text(cell.x + 16, y, cell.text, { size: 7.5, color: INK });
      }
      y += 2;
    }
    if (c.table) y = drawTable(pdf, c.table, y, cw, newPage);
    y += 16;
    onPage++;
  }

  // ── footer: title and page numbers ──
  const total = pdf.pageCount;
  const footTitle = printable(b.title);
  const shortTitle = pdf.fit(footTitle, cw - 90, 8, false, true);
  for (let i = 0; i < total; i++) {
    pdf.setPage(i);
    pdf.line(MARGIN_X, H - 32, W - MARGIN_X, H - 32, RULE, 0.5);
    pdf.text(MARGIN_X, H - 20, shortTitle, { size: 8, color: MUTED });
    pdf.text(W - MARGIN_X, H - 20, b.labels.page(i + 1, total), { size: 8, color: MUTED, align: 'right' });
  }
  return pdf.toBytes(footTitle);
}

/** more: a continued line of the cell above (no swatch). */
interface StatCell { x: number; text: string; color: string; dash: number[]; more?: boolean; }

const TABLE_ROW = 11;
const TABLE_FIRST_COL = 0.4;
/** A wrapped line of a wrapping table's cell. */
const TABLE_LINE = 9.5;
/** A text item's paragraphs: font size and line height. */
const TEXT_SIZE = 8.5;
const TEXT_LH = 11;

function tableHeight(t: PdfTable): number {
  return 6 + (t.title ? 14 : 0) + (t.head.length ? 13 : 0) + t.rows.length * TABLE_ROW + (t.note ? 12 : 0);
}

/** A table's columns and its cells' wrapped lines (drawTable and the look-ahead below share them). */
function tableLayout(pdf: PdfWriter, t: PdfTable, cw: number) {
  const H = pdf.height;
  const n = t.head.length;
  const wraps = !!t.cols;
  // columns: left edge, width, alignment
  const cols: { x: number; w: number; align: 'left' | 'right' }[] = [];
  if (t.cols) {
    const sum = t.cols.reduce((a, c) => a + c.w, 0) || 1;
    let x = MARGIN_X;
    for (const c of t.cols) { const w = cw * c.w / sum; cols.push({ x, w, align: c.align ?? 'left' }); x += w; }
  } else {
    const firstW = cw * TABLE_FIRST_COL;
    const count = Math.max(n, ...t.rows.map(r => r.cells.length));
    const colW = count > 1 ? (cw - firstW) / (count - 1) : 0;
    cols.push({ x: MARGIN_X, w: firstW, align: 'left' });
    for (let i = 1; i < count; i++) cols.push({ x: MARGIN_X + firstW + (i - 1) * colW, w: colW, align: 'right' });
  }
  const lines = (i: number, text: string): string[] => {
    const c = cols[i];
    return c ? pdf.wrap(text, c.w - 6, 8) : [];
  };
  /** A row's height (a wrapped row as high as its longest cell; without the hairline below it). */
  const rowH = (r: PdfTable['rows'][number]) =>
    wraps ? TABLE_ROW + (Math.max(1, ...r.cells.map((c, i) => c ? lines(i, c).length : 1)) - 1) * TABLE_LINE : TABLE_ROW;
  /** The whole wrapping table (for keeping it together). */
  const wrappedH = () => (t.title ? 14 : 0) + (n ? 13 : 0) + (t.note ? 12 : 0)
    + t.rows.reduce((a, r) => a + rowH(r) + 1.5, 0);
  /** Kept on one page: a wrapping table of up to a third of a page (a component's settings). */
  const keepTogether = () => wraps && wrappedH() <= (H - TOP - BOTTOM) / 3;
  /** Title and header stay with the first row. */
  const headH = () => (t.title ? 14 : 0) + (n ? 13 : 0) + (t.rows.length ? rowH(t.rows[0]) : 0);
  return { n, wraps, cols, lines, rowH, wrappedH, keepTogether, headH };
}

/** How far down from y a table must fit on the page for drawTable to start it there: the whole table where it is
 *  kept together, else its title, header and first row. */
function tableLeadHeight(pdf: PdfWriter, t: PdfTable, cw: number): number {
  const L = tableLayout(pdf, t, cw);
  return 6 + (L.keepTogether() ? L.wrappedH() : L.headH());
}

/** How far down from y a text item must fit to start on this page: its title, the paragraphs below it (a bold one,
 *  a heading, with the next block; the next plain one with its first two lines) up to the lead of the first table. */
function textLeadHeight(pdf: PdfWriter, c: PdfText, cw: number): number {
  let need = 16;
  for (let k = 0; k < c.blocks.length; k++) {
    const block = c.blocks[k];
    if ('table' in block) return need + tableLeadHeight(pdf, block.table, cw);
    const lines = pdf.wrap(block.text, cw - (block.bullet ? 10 : 0), TEXT_SIZE, !!block.bold).length;
    const whole = k === 0 || !!block.bold;
    need += (block.gap ?? 2) + (whole ? lines : Math.min(2, lines)) * TEXT_LH;
    if (!whole) break;
  }
  return need;
}

/** Draw `t` from y (the top) down; a row that does not fit continues on a new page (`newPage` → its top),
 *  with the header again. Returns the y below it. */
function drawTable(pdf: PdfWriter, t: PdfTable, y: number, cw: number, newPage: () => number): number {
  const H = pdf.height;
  const { n, wraps, cols, lines, rowH, wrappedH, keepTogether, headH } = tableLayout(pdf, t, cw);
  /** Text of column i at baseline yy (cut to the column, or the lines of a wrapped cell). */
  const cell = (i: number, yy: number, text: string, size: number, bold: boolean, color: Rgb) => {
    const c = cols[i];
    if (!c) return;
    // the first column keeps 6 pt to the next, right-aligned ones 4 pt to the previous; wrapped columns 6 pt
    const room = c.w - (c.align === 'right' ? 4 : 6);
    const x = c.align === 'right' ? c.x + c.w : c.x;
    pdf.text(x, yy, pdf.fit(text, room, size, bold), { size, bold, color, align: c.align });
  };
  const head = () => {
    if (!n) return;
    y += 10;
    t.head.forEach((h, i) => cell(i, y, h, 7.5, true, MUTED));
    y += 3;
    pdf.line(MARGIN_X, y, MARGIN_X + cw, y, RULE, 0.5);
  };
  y += 6;
  // a wrapping table of up to a third of a page (a component's settings) is kept on one page
  if (y > TOP + 6 && keepTogether() && y + wrappedH() > H - BOTTOM) y = newPage() + 6;
  // title and header not alone at the bottom of a page: with the first row as high as it really is
  if ((t.title || n) && y > TOP + 6 && y + headH() > H - BOTTOM) y = newPage();
  if (t.title) {
    y += 11; pdf.text(MARGIN_X, y, pdf.fit(t.title, cw, 9, true), { size: 9, bold: true, color: INK }); y += 3;
  }
  head();
  for (const r of t.rows) {
    const cellLines = wraps ? r.cells.map((c, i) => c ? lines(i, c) : []) : [];
    const h = rowH(r);
    if (y + h > H - BOTTOM) { y = newPage(); head(); }
    if (wraps) {
      cellLines.forEach((ls, i) => ls.forEach((line, k) => {
        const c = cols[i];
        pdf.text(c.align === 'right' ? c.x + c.w - 4 : c.x, y + TABLE_ROW - 2 + k * TABLE_LINE, line, { size: 8, color: INK, align: c.align });
      }));
      y += h;
      // a hairline between wrapped rows (several lines each: which belong together)
      pdf.line(MARGIN_X, y + 1, MARGIN_X + cw, y + 1, [233, 236, 239], 0.4);
      y += 1.5;
      continue;
    }
    y += TABLE_ROW;
    r.cells.forEach((text, i) => { if (text) cell(i, y - 2, text, 8, false, INK); });
    if (r.bar != null && cols.length > 1) {
      // a gauge left in the second column: track, then the share in the row's colour
      const colW = cols[1].w;
      const gx = cols[1].x + 8, gw = Math.max(10, colW - 44);
      pdf.rect(gx, y - 6, gw, 3.5, [233, 236, 239]);
      if (r.bar > 0) pdf.rect(gx, y - 6, Math.max(0.6, gw * Math.min(1, r.bar)), 3.5, hexRgb(r.color ?? '#0ca678'));
    }
  }
  if (t.note) {
    const noteLines = wraps ? pdf.wrap(t.note, cw, 7.5) : [pdf.fit(t.note, cw, 7.5)];
    y += wraps && t.rows.length ? 2 : 0;
    for (const line of noteLines) {
      if (y + 12 > H - BOTTOM) y = newPage();
      y += wraps ? 10 : 12;
      pdf.text(MARGIN_X, y - 2, line, { size: 7.5, color: MUTED });
    }
  }
  return y;
}

/** Stats in two columns (one if a text is too long for half the width, wrapped where it is longer than the line). */
function layoutStats(pdf: PdfWriter, c: PdfChart, cw: number): StatCell[][] {
  const texts = c.stats.map(s => ({ ...s, full: `${s.label}: ${s.text}` }));
  const half = (cw - 12) / 2;
  const twoCols = texts.every(s => pdf.textWidth(s.full, 7.5) <= half - 16);
  const colW = twoCols ? half : cw;
  const rows: StatCell[][] = [];
  texts.forEach((s, i) => {
    const col = twoCols ? i % 2 : 0;
    if (col === 0) rows.push([]);
    const x = MARGIN_X + col * (half + 12);
    const lines = twoCols ? [s.full] : pdf.wrap(s.full, colW - 16, 7.5);
    lines.forEach((text, k) => {
      if (k) rows.push([]);
      rows[rows.length - 1].push({ x, text: pdf.fit(text, colW - 16, 7.5), color: s.color, dash: s.dash, more: k > 0 });
    });
  });
  return rows;
}

/** "#0d6efd" / "rgba(…)" → RGB 0…255 (black for anything else). */
function hexRgb(color: string): Rgb {
  const m = /^#([0-9a-f]{6})$/i.exec(color.trim());
  if (m) { const v = parseInt(m[1], 16); return [(v >> 16) & 255, (v >> 8) & 255, v & 255]; }
  const r = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(color);
  return r ? [+r[1], +r[2], +r[3]] : [0, 0, 0];
}
